import AcceptedIcons from '@/assets/SVGs/icons/AcceptedIcons';
import PendingActionIcon from '@/assets/SVGs/icons/PendingActionIcon';
import RejectedIcon from '@/assets/SVGs/icons/RejectedIcon';
import AppButton from '@/components/ui/AppButton';
import { axiosPatchRequest } from '@/services/Request';
import { endpoints } from '@/services/endpoints';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import BookDetails from './BookDetails';
import EditBookModal from './EditBookModal';
import { X, Check, Clock, XCircle, Pencil } from 'lucide-react';

export default function BookActionModal({ bookId, ...args }) {
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);

  const updateBookStatus = async (status: string) => {
    try {
      await axiosPatchRequest(endpoints.updateBookStatus, { bookStatus: status }, { id: bookId });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
      args.setOpen(false);
    } catch (error) {
      console.log('🚀 ~ updateBookStatus ~ error:', error);
      toast.error(error as string);
    }
  };

  if (!args.open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm"
        onClick={() => args.setOpen(false)}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl border border-coal-200 bg-white shadow-dropdown animate-in fade-in-0 zoom-in-95 duration-200">
          {/* Header with actions */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-coal-200 bg-white/95 px-5 py-3 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <AppButton
                onClick={() => setShowEditModal(true)}
                theme="primary"
                size="sm"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </AppButton>
              <AppButton
                onClick={() => updateBookStatus('approved')}
                theme="success"
                size="sm"
              >
                <Check className="h-4 w-4" />
                Approve
              </AppButton>
              <AppButton
                onClick={() => updateBookStatus('rejected')}
                theme="error"
                size="sm"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </AppButton>
              <AppButton
                onClick={() => updateBookStatus('pending')}
                theme="default"
                size="sm"
              >
                <Clock className="h-4 w-4" />
                Pending
              </AppButton>
            </div>
            <button
              className="rounded-md p-1.5 text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-600"
              onClick={() => args.setOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <BookDetails bookId={bookId} />
          </div>
        </div>
      </div>

      {bookId && (
        <EditBookModal
          bookId={bookId}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificBook] });
            queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
          }}
        />
      )}
    </>
  );
}
